import React, { useEffect, useCallback, useState, useMemo, Suspense, Fragment } from "react";
import { Header, Icon, Button, Divider, Message, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import SchemaForm from "../../SchemaForm";
import DelegateProcessingModal from "../../DelegateProcessingModal";
import { useDispatch } from "react-redux";
import { createInitializeObservatioProcessFormAction, createObservationProcessFormDataUpdateAction, createRequestFetchSaveObservationProcessFormAction } from "../../../store/actions/observationActions";
import { useObservationProcessForm } from "../../../store/selectors/observationSelectors";
import { useLoggedInUser } from "../../../auth/store/authSelectors";
import { isSubmitErrored, isSubmitted, isSubmitting } from "../../../../../sg-core";
import { useHistory, Redirect } from "react-router-dom";

import defaultProcessingJSONSchema from "../../../constants/processForm.schema.json"
import { useApplicationConfiguration } from "../../../configuration/ConfigurationContext";
import IObservation from "../../../interfaces/IObservation";
import { getLatestProcessingMarking, validateProcessingSchema } from "../../../utils/havainnot.helper";
import { getRouteHrefById } from "../../../../../routing";
import { HavainnotRouteId } from "../../../../routes";
import { IFormSchema } from "@/@auditoinnit/auditoinnit/interfaces/IFormSchema";
import { RenderFormDefinition } from "../../formType/RenderFormDefinition";
import ICategory from "@/@auditoinnit/auditoinnit/interfaces/ICategory";

interface ProcessingFormContentProps extends IProcessingForm {
    customFormSchema?: IFormSchema;

}

const ProcessingFormContent: React.FC<ProcessingFormContentProps> = ({ observation, customFormSchema }) => {
    const config = useApplicationConfiguration();
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const processForm = useObservationProcessForm()
    const user = useLoggedInUser()
    const isOnEditing = Boolean(processForm)

    const [isModalVisible, setModalVisibility] = useState(false);
    const toggleModalVisibility = () => setModalVisibility(!isModalVisible)
    const latestMarking = useMemo(() => getLatestProcessingMarking(observation), [observation]);

    const customFormSchemaOk = useMemo(() => customFormSchema && validateProcessingSchema(customFormSchema), [customFormSchema]);

    // Initialize form on load if not already
    useEffect(() => {
        if (user && (!processForm || processForm.data.observationId !== observation.id)) {
            const processingMarkingPublicity = latestMarking?.formData.kasittelymerkintojen_julkisuus || config.processingMarkingPublicityDefaultValue;
            dispatch(createInitializeObservatioProcessFormAction(
                observation,
                user.id,
                processingMarkingPublicity,
                latestMarking?.formData.luokitteluteksti
            ));
        }
    }, [dispatch]); // eslint-disable-line


    //const closeProcessForm = () => { processForm && dispatch(createClearObservationProcessForm()) }
    const openProcessForm = useCallback(() => { user && dispatch(createInitializeObservatioProcessFormAction(observation, user.id, config.processingMarkingPublicityDefaultValue)) }, [dispatch, observation.id, user, config.processingMarkingPublicityDefaultValue])
    const handleGoBack = useCallback(() => { history.goBack() }, [history])

    const handleSubmit = useCallback((values: { [key: string]: any }) => {
        dispatch(createObservationProcessFormDataUpdateAction(values));
        dispatch(createRequestFetchSaveObservationProcessFormAction(customFormSchema))
    }, [dispatch, customFormSchema]);

    if (processForm && isSubmitted(processForm)) {
        return <Redirect to={getRouteHrefById(HavainnotRouteId.WorklistPage)!} />
    }

    const initialFormData = processForm ? processForm.data.formData : {}


    const renderSchemaForm = (schema: IFormSchema) => (
        <SchemaForm
            formSchema={(schema as any)}
            onSubmit={handleSubmit}
            formData={initialFormData}
            canViewNames
        >
            <Divider />
            <div>
                <Button fluid primary type="submit" icon loading={processForm && isSubmitting(processForm)}> <Icon name="check" /> {t("Tallenna")} </Button>
            </div>

            <div style={{ marginTop: '10px' }}>
                <Button type="button" onClick={handleGoBack} fluid> {t("Peruuta")}  </Button>
            </div>
        </SchemaForm>
    );

    return (
        <>
            <Header as="h3"> {t("Käsittelijän lomake")}</Header>
            <div>
                <DelegateProcessingModal
                    observationGuid={observation.guid}
                />
            </div>
            <br />
            <p> {t("Tallenna kirjoittamasi lisätiedot ennen kuin välität havainnon toiselle käsittelijälle.")}</p>
            {processForm && isSubmitErrored(processForm) ? <Message content={t("Tallennus epäonnistui!")} negative /> : null}
            {isOnEditing ?
                <Fragment>
                    {customFormSchema ? (
                        customFormSchemaOk ? renderSchemaForm(customFormSchema) : <Message content="Failed to validate custom observation processing form" />
                    ) : renderSchemaForm(defaultProcessingJSONSchema as IFormSchema)}
                </Fragment>
                : <Button primary onClick={openProcessForm}> {t("Luo uusi käsittely merkintä")}  </Button>}
        </>
    );
};


interface IProcessingForm {
    observation: IObservation;
    category: ICategory;

}

const ProcessingForm: React.FC<IProcessingForm> = (props) => {
    const customFormSchema = props.category.processingFormDefinition;

    if (props.observation.processingFormSchema) {
        return (
            <ProcessingFormContent {...props} customFormSchema={props.observation.processingFormSchema} />
        );
    };

    if (customFormSchema) {
        return (
            <Suspense fallback={<Segment loading basic />}>
                <RenderFormDefinition
                    search={{ formDefinition: customFormSchema }}
                    render={({ formDefinition }) => (
                        formDefinition && <ProcessingFormContent {...props} customFormSchema={formDefinition.schema} />
                    )}
                />
            </Suspense>
        )
    };

    return (
        <ProcessingFormContent {...props} />
    );
}

export default ProcessingForm;

